/* bilance — čistá matematika „živé bilance“ z dat analýzy (financial_maps.data.v2).
 *
 * Vyextrahováno z pages/AnalyzaV2Page.js (computeBilance) BEZ ZMĚNY METODIKY,
 * aby šlo jádro testovat bez načítání celé (React) stránky. Stránka i tiskový
 * výstup (AnalyzaTiskPage) volají computeBilance, který deleguje sem a jen
 * dolokalizuje texty vlajek přes bilanceFlagMessage().
 *
 * ⚠️ Metodika M2 (drift): backend/lib/finmap_signals.py:bilance() zrcadlí
 * PODMNOŽINU těchto vzorců (jen CF/rezerva, ne čisté jmění). Prahy (rezerva 3/6×,
 * DSTI 40 %, odkládání — minimum 10 % od 4. 10. 2026) jsou stejné; net-worth
 * pole backend nepočítá.
 * Při změně vzorců zde je nutné rozhodnout o synchronizaci s backendem.
 *
 * ⚠️ 3. 10. 2026 (audit A1.1 + A1.2, „jedno číslo všude“): rezerva se dělí
 * NEZBYTNÝMI výdaji + splátkami a peníze po ruce bere z `kbelikyUctu` (jako
 * plán); míra odkládání má jeden vzorec `miraOdkladani` (schodek 0 %,
 * neznámé `null`). Backend `finmap_signals.py` tyhle dvě změny zatím NEMÁ.
 *
 * ⚠️ 4. 10. 2026 (dráha F4): míra odkládání bere ODSOUHLASENOU částku
 * (`d.prebytek`) přednostně před volnými penězi z dat — jedna definice pro
 * lištu, vodopád, panel, kino, Návrh, plán i prezentace. Backend
 * `finmap_signals.py` (signál `low_saving_rate`) ji zrcadlí — platí až po
 * redeployi na Emergentu.
 *
 * ⚠️ 5. 10. 2026 (nález M1-11, majitel „ano“): PŘÍSPĚVEK ZAMĚSTNAVATELE na
 * penzijko se počítá do odkládání I DO PŘÍJMU. Z čistého příjmu domácnosti
 * neodchází (není ve výdajích), takže dřív zvedal jen čitatel:
 *   míra = (volné + vlastní vklady + zaměstnavatel) / (čistý příjem + zaměstnavatel)
 * Příjem 40 000, výdaje 37 000, penzijko 500 + zaměstnavatel 1 000:
 * 4 500 / 41 000 = 10,98 % (dřív 4 500 / 40 000 = 11,25 %). Backend
 * `finmap_signals.py` totéž (živě po redeployi na Emergentu).
 */

import { asArr, asObj, asCislo, sberProblemu } from "./bezpecneHodnoty";
/* Formát desetinného čísla podle jazyka — česky „4,8“, anglicky „4.8“. */
import { localeJazyka } from "./i18n/format";
/* Kdo je v analýze skutečná osoba (identita, ne prázdný řádek) — jeden zdroj. */
import { detiZAnalyzy } from "./osobyZAnalyzy";
/* Peníze po ruce počítá plán (`kbelikyUctu`) — analýza je bere odtud, ne po
 * svém (audit A1.1, 3. 10. 2026). planSolver importuje jen bezpecneHodnoty,
 * horizontCile a doporuceniCile, takže kruh nevzniká. */
import { kbelikyUctu } from "./planSolver";
/* Regulátor u textu zadluženosti podle ZEMĚ TENANTA, ne jazyka (R6, 4. 10. 2026). */
import { zemeTenanta, normZeme } from "./tenantMena";
/* Razítko metodiky plánu — míra odkládání v plánu (dráha F4). Modul je bez
 * závislostí, kruh nevzniká. */
import { platiZmena, verzePlanu, METODIKA_2026_10_05 } from "./metodikaPlanu";
/* DSTI je u ČNB DOPORUČENÍ 40 % (horní hranice deaktivovaná) — práh vlajky
 * i číslo v textu z jednoho místa (velká kontrola M1-09, 5. 10. 2026). */
import { CONFIG_2026 } from "../data/config2026";

/* Suma pole přes řádky (koerce na číslo jako `+r[f] || 0` v JS — string/null/undefined → 0).
 * `asArr` místo `(arr || [])`: pojistka `|| []` chránila jen proti null/undefined,
 * takže objekt z rozbité odpovědi API shodil `.reduce` a s ním celou obrazovku. */
const S = (arr, f = "hodnota") => asArr(arr).reduce((a, r) => a + (+r[f] || 0), 0);

/**
 * VOLNÉ CASH FLOW — JEDEN VÝPOČET PRO CELOU ANALÝZU (audit 29. 9. 2026).
 *
 * Do 29. 9. 2026 ho formulář analýzy počítal jen z „obvyklých výdajů“, zatímco
 * prezentace, plovoucí bilance i plán braly větší z hodnot „obvyklé“ a „součet
 * rozpisu“. Když byl rozpis vyšší a poradce neklikl na „použít“, klient
 * odsouhlasil jiné číslo („Souhlasíte, že X měsíčně…“), než jaké pak viděl na
 * plátně a v plánu. Teď všechna místa volají tuhle funkci.
 *
 * Výdaje = max(obvyklé, součet kategorií) — TÁŽ definice jako `soucetVydaju`
 * (analyzaV2/stavy.js), ze které čte plán (planEngine) i most (souhrny.js).
 *
 * `prebytek` NENÍ `volneCF`: bez příjmů nebo bez výdajů je `null` = „nezadáno“.
 * Z „příjem 60 000, výdaje 0“ by jinak vyšlo, že klient má volných 60 000
 * (stejné pravidlo jako `stavCashflow` a `prebytekDopocet` v plánu).
 * `volneCF` zůstává číslem kvůli vlajkám a poměrům bilance níž.
 * @param {object} d  data analýzy (v2)
 */
export function volneCashflow(d = {}) {
  const src = asObj(d);
  const p2 = asObj(src.prijmy2);
  const prijmy =
    (+p2.prokaz1 || 0) + (+p2.prokaz2 || 0) + (+p2.ostatni1 || 0) +
    (+p2.ostatni2 || 0) + (+p2.pasivni || 0) + (+p2.bonusyRok || 0) / 12;
  const v2 = asObj(src.vydaje2);
  const vydaje = Math.max(
    +v2.obvykle || 0,
    Object.values(asObj(v2.detail)).reduce((a, v) => a + (+v || 0), 0)
  );
  const splatky = S(src.zavazkyB, "splatka") + S(src.zavazkyO, "splatka");
  const volneCF = prijmy - vydaje - splatky;
  const znamo = prijmy > 0 && vydaje > 0;
  return { prijmy, vydaje, splatky, volneCF, znamo, prebytek: znamo ? volneCF : null };
}

/**
 * NEZBYTNÉ VÝDAJE, JAK JE ZADAL PORADCE — kladné číslo, jinak `null`.
 * Stejné pravidlo jako plán (`nezbytneZadane` v planEngine): výchozí analýza
 * má `vydaje2.minimalni = 0`, takže nula a „nevyplněno“ se nedají rozeznat
 * a obojí znamená NEVÍME. Z nuly by rezerva vyšla jen ze splátek
 * (vietnamská rodina: (0 + 16 500) × 6 = 99 000 Kč, audit A1.3).
 * @param {object} d data analýzy (v2)
 * @returns {number|null}
 */
export function nezbytneVydaje(d = {}) {
  const n = asCislo(asObj(asObj(d).vydaje2).minimalni);
  return n != null && n > 0 ? n : null;
}

/**
 * PENÍZE PO RUCE V ANALÝZE (audit A1.1, 3. 10. 2026).
 * Spořicí účet + běžný účet + hotovost podle `kbelikyUctu` z planSolveru.
 * Termínovaný vklad a účet v cizí měně do rezervy NEPATŘÍ, dokud u nich
 * poradce nezaškrtne „Okamžitě dostupné“ — do 3. 10. je lišta počítala,
 * plán ne, a na jednom plátně stála dvě různá čísla rezervy.
 * Stavebko ani penzijko tady rezerva nejsou (vázací lhůta, výběr až v 60).
 * ⚠️ OD Č20 (4. 10. 2026) TO UŽ NENÍ TOTÉŽ ČÍSLO JAKO PLÁN: plán čerpá na
 * rezervu „hned“ i termínovaný vklad a stavební spoření (planSolver
 * `PORADI_OKAMZITE`, plneniCilu.stavRezervy přes `hnedKDispozici`) — pokyn
 * majitele Č20. U klienta s účty 170 000 a stavebkem 200 000 ukáže analýza po
 * ruce 170 000 (3,4 měsíce), plán rezervu 300 000 = 100 %. Které číslo má
 * platit jako stav DNES, čeká na rozhodnutí majitele (velká kontrola M1-03).
 * @param {Array} ucty řádky `majetek.ucty`
 */
export function penizePoRuce(ucty) {
  const kb = kbelikyUctu(asArr(ucty));
  return kb.sporici + kb.bezny + kb.hotovost;
}

/**
 * PRAVIDELNÉ VKLADY — kolik každý měsíc jde na spoření
 * (stavebko, penzijko vč. příspěvku zaměstnavatele, fondy, KŽP, spořicí účty).
 * Definice beze změny proti dřívějšímu `sporeniMes` bilance. Z toho
 * `prispevekZamestnavatele` z příjmu domácnosti NEODCHÁZÍ — míra odkládání
 * ho proto přičte i k příjmu (5. 10. 2026, M1-11).
 * @param {object} d data analýzy (v2)
 */
export function pravidelneVklady(d = {}) {
  const m = asObj(asObj(d).majetek);
  /* Tvar `const sporeniMes = S(m.<kategorie>, "<klíč>") + …` čte hlídač
   * lib/analyzaV2/__tests__/kliceProtiBilanci.test.js — klíče musí sedět na
   * pole formuláře. Neměnit na jiný zápis bez úpravy hlídače. */
  const sporeniMes =
    S(m.stavebko, "vklad") + S(m.penzijko, "vklad") + S(m.penzijko, "zam") +
    S(m.opf, "vklad") + S(m.kzp, "vklad") + S(m.ucty, "vklad");
  return sporeniMes;
}

/**
 * Kolik příspěvku zaměstnavatele míra odkládání započte — JEDNO PRAVIDLO pro
 * čitatel i základ: nejméně 0 a nejvýš tolik, kolik jsou pravidelné vklady
 * celkem (příspěvek mimo čitatel by míru jen tiše snížil). Rozjede se to jen
 * u rozbitých dat se záporným vkladem na jiném řádku — dřív tam míra
 * příspěvek ořízla, ale „10 % = X“ v kinech a novém CRM ne (dočištění M1-11,
 * 5. 10. 2026). Backend: finmap_signals.py `zamestnavatel = min(…)`.
 * @param {number} zamestnavatel příspěvek zaměstnavatele / měs
 * @param {number} vklady pravidelné vklady / měs (vč. zaměstnavatele)
 * @returns {number}
 */
export function zamestnavatelVMire(zamestnavatel, vklady) {
  const vk = Math.max(0, asCislo(vklady) || 0);
  return Math.min(vk, Math.max(0, asCislo(zamestnavatel) || 0));
}

/**
 * PŘÍSPĚVEK ZAMĚSTNAVATELE na penzijko měsíčně (pole `zam` u řádků
 * `majetek.penzijko`), nejméně 0 a nejvýš `pravidelneVklady`
 * (`zamestnavatelVMire`). Je SOUČÁSTÍ `pravidelneVklady` — a protože
 * z čistého příjmu domácnosti neodchází, přičítá se míra odkládání i k příjmu
 * (majitel 5. 10. 2026: „ano“, nález M1-11).
 * @param {object} d data analýzy (v2)
 * @returns {number}
 */
export function prispevekZamestnavatele(d = {}) {
  return zamestnavatelVMire(S(asObj(asObj(d).majetek).penzijko, "zam"), pravidelneVklady(d));
}

/**
 * PRAHY ODKLÁDÁNÍ — JEDNO MÍSTO PRO CELOU APPKU (majitel 4. 10. 2026:
 * „10 % minimum, 20 ideál, 30 splní cíle hodně“, schváleno „ok vse dle
 * doporučení“, bod 9). Do té doby měla appka „minimum 20 %, cíl 30 %“
 * (doporuceniZAnalyzy) a jinde natvrdo „aspoň 20 %“ (lišta, kino, tisk,
 * rozpis výdajů, plán) — klient s 15 % četl na jednom místě „pod laťkou“
 * a poradce mu přitom říkal „minimum máte“.
 *
 *   pod      < 10 %   pod minimem (varování)
 *   minimum  10–19 %  minimum splněné
 *   ideal    20–29 %  ideál
 *   rychle   ≥ 30 %   cíle se plní hodně rychle
 *
 * Barva má jeden význam: zelená = minimum splněné (≥ 10 %), jinak varování.
 * Pásmo (minimum / ideál / rychle) říká text, ne další barva.
 * Hranici posuzuje vždy ZOBRAZENÉ (zaokrouhlené) procento.
 *
 * ⚠️ 20 % jako MODELOVÝ PŘEBYTEK plánu bez výdajů (lib/prebytekPlanu.js, Č9)
 * je jiná věc — předpoklad o penězích, ne práh. Tady se nemění.
 */
export const ODKLAD_MIN_PCT = 10;
export const ODKLAD_IDEAL_PCT = 20;
export const ODKLAD_RYCHLE_PCT = 30;
/** Tři hladiny v pořadí, jak je poradce říká (kino, posuvník, čipy). */
export const HLADINY_ODKLADANI = Object.freeze([
  Object.freeze({ pct: ODKLAD_MIN_PCT, klic: "minimum" }),
  Object.freeze({ pct: ODKLAD_IDEAL_PCT, klic: "ideal" }),
  Object.freeze({ pct: ODKLAD_RYCHLE_PCT, klic: "rychle" }),
]);

/**
 * Pásmo odkládání pro zobrazené procento.
 * @param {number|null} procento  celé procento (`miraOdkladani().procento`)
 * @returns {"pod"|"minimum"|"ideal"|"rychle"|null} `null` = nevíme (nezadáno)
 */
export function pasmoOdkladani(procento) {
  const p = asCislo(procento);
  if (p == null) return null;
  if (p >= ODKLAD_RYCHLE_PCT) return "rychle";
  if (p >= ODKLAD_IDEAL_PCT) return "ideal";
  if (p >= ODKLAD_MIN_PCT) return "minimum";
  return "pod";
}

/** Je splněné aspoň minimum? `null` = nevíme (nezadáno ≠ nesplněno). */
export function minimumOdkladaniSplneno(procento) {
  const pasmo = pasmoOdkladani(procento);
  return pasmo == null ? null : pasmo !== "pod";
}

/**
 * MÍRA ODKLÁDÁNÍ Z HOTOVÝCH ČÍSEL — jediný vzorec (audit A1.2, 3. 10. 2026).
 *
 * Do 3. 10. ji appka počítala třikrát a jinak: lišta a tisk (vklady + kladné
 * volné peníze) 39 %, prezentace a kino (jen volné peníze) 28 %, u rodiny se
 * schodkem lišta 2 %, prezentace 0 % a kino −12 %. Teď všechna místa volají
 * tohle (přes `miraOdkladani` nebo `odkladani` v analyzaV2/penizeKina.js).
 *
 *   odklad   = pravidelné vklady (vč. příspěvku zaměstnavatele) + volné peníze
 *   základ   = příjmy + příspěvek zaměstnavatele (5. 10. 2026, M1-11 — z čistého
 *              příjmu neodchází, takže patří i do jmenovatele)
 *   procento = odklad / základ
 *   SCHODEK  → 0 % (rodina v minusu ve skutečnosti nic neodkládá — vklady
 *              platí z dluhu nebo z úspor)
 *   NEZNÁMO  → null: bez příjmů nebo bez výdajů (`volne` = null) se procento
 *              netvrdí, obrazovka ukáže „—“ (dřív „100 %“ zeleně u klienta,
 *              který má zadaný jen příjem).
 *
 * @param {{ prijmy: number, volne: number|null, vklady?: number, zamestnavatel?: number }} c
 *   `volne` = volné peníze (`volneCashflow().prebytek`), nebo odsouhlasená
 *   částka, když se počítá „kolik půjde stranou“ podle dohody s klientem.
 *   `vklady` = VŠECHNY pravidelné vklady (`pravidelneVklady`, i se
 *   zaměstnavatelem), `zamestnavatel` = kolik z nich platí zaměstnavatel
 *   (`prispevekZamestnavatele`) — přičte se k příjmu. Víc, než jsou vklady,
 *   se nepřičte (příspěvek mimo čitatel by míru jen tiše snížil).
 * @returns {{ znamo: boolean, procento: number|null, podil: number|null,
 *   odklad: number|null, vklady: number, volne: number|null, schodek: boolean,
 *   zamestnavatel: number, zaklad: number|null }}
 *   `zaklad` = příjem, ze kterého je procento (null bez příjmu) — obrazovka,
 *   která ukazuje „z 41 000 Kč“, bere tohle číslo.
 */
export function miraOdkladaniZCisel({ prijmy, volne, vklady = 0, zamestnavatel = 0 } = {}) {
  const p = asCislo(prijmy);
  const v = asCislo(volne);
  const vk = Math.max(0, asCislo(vklady) || 0);
  const zam = zamestnavatelVMire(zamestnavatel, vk);
  if (p == null || p <= 0 || v == null) {
    return { znamo: false, procento: null, podil: null, odklad: null, vklady: vk, volne: v, schodek: false, zamestnavatel: zam, zaklad: p != null && p > 0 ? p + zam : null };
  }
  const zaklad = p + zam;
  if (v < 0) return { znamo: true, procento: 0, podil: 0, odklad: 0, vklady: vk, volne: v, schodek: true, zamestnavatel: zam, zaklad };
  const odklad = vk + v;
  const podil = odklad / zaklad;
  return { znamo: true, procento: Math.round(podil * 100), podil, odklad, vklady: vk, volne: v, schodek: false, zamestnavatel: zam, zaklad };
}

/**
 * Základ míry odkládání v Kč — čistý příjem domácnosti + příspěvek
 * zaměstnavatele (`miraOdkladaniZCisel().zaklad`), `null` bez příjmu.
 * Pro obrazovky, které z procent dopočítávají částky („10 % = 4 100 Kč“,
 * „do minima chybí…“) — ať částka sedí na procento.
 * S `vklady` ořízne příspěvek stejně jako míra (`zamestnavatelVMire`); bez
 * nich bere příspěvek tak, jak přišel — `prispevekZamestnavatele(d)` je
 * oříznutý už u zdroje.
 * @param {number} prijmy čistý příjem domácnosti / měs
 * @param {number} [zamestnavatel] příspěvek zaměstnavatele / měs
 * @param {number} [vklady] pravidelné vklady / měs (vč. zaměstnavatele)
 * @returns {number|null}
 */
export function zakladOdkladani(prijmy, zamestnavatel = 0, vklady = undefined) {
  const p = asCislo(prijmy);
  if (p == null || p <= 0) return null;
  const zam = vklady === undefined ? Math.max(0, asCislo(zamestnavatel) || 0) : zamestnavatelVMire(zamestnavatel, vklady);
  return p + zam;
}

/**
 * Odsouhlasená měsíční částka („Souhlasíte, že X měsíčně…“, `d.prebytek`),
 * nebo `null` = poradce ji nevyplnil. Nula je odpověď (klient odsouhlasil,
 * že volné peníze nejsou), prázdno ne — táž definice jako plán
 * (`prebytekZadany` v planEngine, `prebytekZModelu`).
 * @param {object} d data analýzy (v2)
 * @returns {number|null}
 */
export function odsouhlasenaCastka(d = {}) {
  return asCislo(asObj(d).prebytek);
}

/**
 * MÍRA ODKLÁDÁNÍ — JEDNA DEFINICE PRO CELOU APPKU (dráha F4, 4. 10. 2026).
 *
 *   míra = (odsouhlasená částka + vlastní pravidelné vklady + příspěvek
 *           zaměstnavatele) / (čistý příjem domácnosti + příspěvek zaměstnavatele)
 *   bez odsouhlasené částky: (příjmy − výdaje − splátky) místo odsouhlasené částky
 *
 * VLASTNÍ vklady (stavebko, penzijko, fondy, KŽP, spořicí účty) platí
 * domácnost ze svého příjmu — jsou ve výdajích, proto se k volným penězům
 * přičítají. PŘÍSPĚVEK ZAMĚSTNAVATELE na penzijko z čistého příjmu domácnosti
 * neodchází a ve výdajích NENÍ: přičítá se do odkládání i k příjmu (majitel
 * 5. 10. 2026 „ano“, M1-11; do té doby jen do čitatele — míra tak vyšla
 * o kus vyšší). Schodek z dat bez dohody → 0 %; bez příjmů, nebo bez výdajů
 * i dohody → `null` („—“).
 *
 * Do 4. 10. 2026 měla appka dvě definice a k nim ještě dvě jiné v plánu:
 * lišta, vodopád, tisk analýzy, kino 50/30/20 a prezentace analýzy počítaly
 * z dat (19 %), panel „Kolik jde stranou“ a kino „Výdaje a odkládání“ podle
 * dohody (12 %) a Návrh s prezentací plánu jen přebytek bez vkladů (9 %) —
 * pro tutéž rodinu s dohodou 8 000 Kč tři různá procenta. Teď všechna místa
 * volají tuhle funkci (plán s razítkem metodiky 5. 10. 2026 a novějším,
 * `jednaMiraVPlanu`; starší uložené plány drží svá čísla).
 * @param {object} d data analýzy (v2)
 * @returns {ReturnType<typeof miraOdkladaniZCisel> & { odsouhlaseno: boolean, dohoda: number|null }}
 */
export function miraOdkladani(d = {}) {
  const tok = volneCashflow(d);
  const dohoda = odsouhlasenaCastka(d);
  const volne = dohoda != null ? Math.max(0, dohoda) : tok.prebytek;
  return {
    ...miraOdkladaniZCisel({ prijmy: tok.prijmy, volne, vklady: pravidelneVklady(d), zamestnavatel: prispevekZamestnavatele(d) }),
    odsouhlaseno: dohoda != null,
    dohoda,
  };
}

/**
 * Starší jméno téže funkce (panel „Kolik jde stranou“, kino „Výdaje
 * a odkládání“). Od dráhy F4 je to `miraOdkladani` — jedna definice.
 * @param {object} d data analýzy (v2)
 */
export function miraOdkladaniDohodnuta(d = {}) {
  return miraOdkladani(d);
}

/**
 * Ukazuje uložený plán jednu míru odkládání s analýzou?
 * Plán s razítkem metodiky 2026-10-05 a novějším (i plán, který ještě nikdo
 * neuložil) ano. Starší uložený plán drží svá čísla (pravidlo majitele
 * 3. 10. 2026: staré plány se nepřepočítávají) — Návrh tam dál ukazuje
 * přebytek po pojistném / příjmy a prezentace přebytek / příjmy, dokud
 * poradce neklikne „Přepočítat podle nové metodiky“.
 * @param {object} d data analýzy (v2) — rozhoduje `d.navrh.metodika`
 */
export function jednaMiraVPlanu(d = {}) {
  return platiZmena(verzePlanu(asObj(d).navrh), METODIKA_2026_10_05);
}

/**
 * Numerické jádro bilance + jazykově NEUTRÁLNÍ deskriptory vlajek.
 * Deskriptor vlajky: { t: "err"|"warn"|"ok", code, val? } — text dodá bilanceFlagMessage().
 * @param {object} d  data analýzy (v2)
 */
export function computeBilanceCore(d = {}) {
  /* Sběrač polí, která dorazila v jiném TVARU. Bilance se z nich nespočítá —
   * a její nula by v přehledu vypadala jako údaj („nemáte žádné dluhy“),
   * i když se jen nepovedlo načíst data. Proto `dataProblemy` na výstupu. */
  const sber = sberProblemu();
  const m = sber.obj("majetek", d.majetek);
  const ucty = sber.pole("majetek.ucty", m.ucty);
  const zavazkyB = sber.pole("zavazkyB", d.zavazkyB);
  const zavazkyO = sber.pole("zavazkyO", d.zavazkyO);
  const finMajetek = S(ucty) + S(sber.pole("majetek.opf", m.opf)) + S(sber.pole("majetek.stavebko", m.stavebko))
    + S(sber.pole("majetek.penzijko", m.penzijko)) + S(sber.pole("majetek.kzp", m.kzp));
  /* NEFINANČNÍ MAJETEK DNES — BEZ DĚDICTVÍ (29. 9. 2026). Dědictví / budoucí
   * prodej je majetek, který klient DNES nemá (analyzaV2/budouciMajetek.js):
   * do čistého jmění ani do zdrojů „dnes“ nevstupuje a jde ven ZVLÁŠŤ jako
   * `budouci`. Dřív se tu sčítal a jen stránka analýzy ho před voláním
   * odřízla (`bezBudoucihoMajetku`) — Analýza 2.0 beta (realAdapter) a každé
   * další holé volání tak ukázaly čisté jmění i s chalupou po rodičích,
   * kterou klient zdědí za dvacet let. Teď platí JEDNA definice pro všechny. */
  const nefin = S(sber.pole("majetek.nemovitosti", m.nemovitosti)) + S(sber.pole("majetek.firma", m.firma));
  const budouci = S(sber.pole("majetek.dedictvi", m.dedictvi));
  const dluhy = S(zavazkyB, "jistina") + S(zavazkyO, "jistina");
  const cisteJmeni = finMajetek + nefin - dluhy;
  const splatky = S(zavazkyB, "splatka") + S(zavazkyO, "splatka");
  /* Příjmy, výdaje a volné CF z JEDNOHO výpočtu (volneCashflow výš) — týž,
   * ze kterého čte formulář, prezentace i věta k odsouhlasení přebytku. */
  const { prijmy, vydaje, volneCF, prebytek } = volneCashflow(d);
  const sporeniMes = pravidelneVklady(d);
  /* REZERVA = TÝŽ VÝPOČET JAKO PLÁN (audit A1.1, 3. 10. 2026).
   * Peníze po ruce: spořicí + běžný účet + hotovost (`kbelikyUctu`), ne
   * termínovaný vklad. Základ: diktát majitele 6 × (NEZBYTNÉ výdaje + splátky)
   * — dřív se tu dělilo OBVYKLÝMI výdaji a lišta tak ukazovala jiný počet
   * měsíců, než z jakého počítá plán a nápověda u cíle.
   * Bez zadaných nezbytných výdajů se počet měsíců NETVRDÍ: `rezervaZnama`
   * je `false` a obrazovka má ukázat „—“.
   * ⚠️ `rezervaMesicu` zůstává ČÍSLO (0, když nevíme) — Analýza 2.0 beta
   * (design-lab/analyza2/realAdapter.js) odmítne celou bilanci, když tu najde
   * něco jiného než konečné číslo. O tom, jestli je to údaj, rozhoduje
   * `rezervaZnama`, ne nula. */
  const rezervaLikv = penizePoRuce(ucty);
  const nezbytne = nezbytneVydaje(d);
  const rezervaZaklad = nezbytne != null ? nezbytne + splatky : null;
  const rezervaZnama = rezervaZaklad > 0;
  const rezervaMesicu = rezervaZnama ? rezervaLikv / rezervaZaklad : 0;
  const dsti = prijmy > 0 ? splatky / prijmy : 0;
  /* Míra odkládání z JEDNÉ definice (`miraOdkladani`, dráha F4 4. 10. 2026):
   * odsouhlasená částka má přednost před volnými penězi z dat — lišta, tisk
   * analýzy, panel, kino, vodopád i plán ukazují totéž procento. Dřív lišta
   * brala jen data (19 %), zatímco panel a kino dohodu (12 %).
   * `odkladaniRate` zůstává číslo (0, když nevíme — dřív tu u klienta jen
   * s příjmem stálo 1 = 100 %); jestli je to údaj, říká
   * `miraOdkladani.znamo` (procento `null` → „—“). */
  const mira = miraOdkladani(d);
  const odkladaniRate = mira.znamo ? mira.podil : 0;
  const kzpSum = S(m.kzp);
  /* DĚTI = ŘÁDKY S IDENTITOU (audit R6). Prázdný řádek po „Přidat dítě“
   * spouštěl vlajku „děti bez životního pojištění“ u bezdětného klienta. */
  const deti = detiZAnalyzy(d);

  const flags = [];
  if (rezervaZnama) {
    if (rezervaMesicu < 3) flags.push({ t: "err", code: "reserve_low", val: rezervaMesicu });
    else if (rezervaMesicu < 6) flags.push({ t: "warn", code: "reserve_mid", val: rezervaMesicu });
    else flags.push({ t: "ok", code: "reserve_ok", val: rezervaMesicu });
  }
  /* Schodek jen u ZNÁMÉHO volného CF — bez výdajů ho netvrdíme. */
  if (prebytek != null && prebytek < 0) flags.push({ t: "err", code: "cf_negative", val: prebytek });
  if (dsti > CONFIG_2026.HYPO.DSTI / 100) flags.push({ t: "warn", code: "dsti_high", val: dsti });
  /* Hladinu posuzuje ZOBRAZENÉ procento — lišta nesmí u 9,6 % ukázat
   * „10 %“ a zároveň varovat, že je pod 10 %. Práh = MINIMUM (10 %, bod 9
   * 4. 10. 2026); dřív 20 %. */
  if (mira.znamo && !mira.schodek && pasmoOdkladani(mira.procento) === "pod") flags.push({ t: "warn", code: "saving_low", val: mira.podil });
  if (deti.length > 0 && kzpSum === 0) flags.push({ t: "warn", code: "kids_no_life" });

  return {
    finMajetek, nefin, budouci, dluhy, cisteJmeni, splatky, prijmy, vydaje, volneCF,
    /* ZNÁMO / NEZNÁMO (audit A1.1 + A1.2): `rezervaZnama: false` (chybí
     * nezbytné výdaje) a `miraOdkladani.znamo: false` (chybí příjmy nebo
     * výdaje) = obrazovka kreslí „—“, ne nulu z `rezervaMesicu`/`odkladaniRate`. */
    sporeniMes, rezervaLikv, rezervaZaklad, rezervaZnama, rezervaMesicu, dsti, odkladaniRate,
    miraOdkladani: mira, flags,
    /* Prázdné = data v pořádku. Neprázdné = čísla výš jsou v těch místech
     * nulová jen proto, že se nedala spočítat — přiznat, ne vydávat za údaj. */
    dataProblemy: sber.problemy,
  };
}

/**
 * Lokalizovaný text vlajky. Bere `t` z LanguageContextu, ne příznak `sk` —
 * s tím totiž Ukrajinec, Vietnamec i Angličan dostali češtinu, a to i v
 * TIŠTĚNÉ ANALÝZE, kterou si klient odnáší ze schůzky (nález 8/2026).
 * Desetinné číslo formátuje `Intl` podle jazyka — česky „4,8“, ne „4.8“.
 * @param {object} f   deskriptor { t, code, val? }
 * @param {object} opt { t: (key, params) => string, kc: (n)=>string, zeme?: string }
 *   `zeme` = země tenanta (bez ní `zemeTenanta()`). Rozhoduje o REGULÁTORU
 *   u zadluženosti: „doporučení ČNB do 40 %“ je české pravidlo — slovenský
 *   tenant (v jakémkoli jazyce) ho nesmí číst. NBS 40 % nemá (její DSTI je
 *   60 % z příjmu po odečtení životního minima), proto u SK text bez
 *   regulátora a bez čísla (R6, 4. 10. 2026). Do 5. 10. 2026 text tvrdil
 *   „limit ČNB 40–45 %“ — DSTI je u ČNB deaktivované, 40 % je doporučení
 *   (data/config2026.js), slovo „limit“ patří jen k LTV (velká kontrola M1-09).
 */
export function bilanceFlagMessage(f, { t, kc = (n) => String(n), zeme } = {}) {
  if (typeof t !== "function") return "";
  const n = new Intl.NumberFormat(localeJazyka(t.lang || "cs"), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Number.isFinite(f.val) ? f.val : 0);
  switch (f.code) {
    case "reserve_low":
      return t("bilanceFlag.reserveLow", { n });
    case "reserve_mid":
      return t("bilanceFlag.reserveMid", { n });
    case "reserve_ok":
      return t("bilanceFlag.reserveOk", { n });
    case "cf_negative":
      return t("bilanceFlag.cfNegative", { castka: kc(-f.val) });
    case "dsti_high":
      /* „doporučení ČNB do 40 %“, ne „limit 40–45 %“ (M1-09): DSTI je deaktivované,
       * slovo „limit“ patří jen k LTV (uverLimity.js), číslo z CONFIG_2026. */
      return t(normZeme(zeme || zemeTenanta()) === "CZ" ? "bilanceFlag.dstiHigh" : "bilanceFlag.dstiHighBezRegulatora", { pct: Math.round(f.val * 100), max: CONFIG_2026.HYPO.DSTI });
    case "saving_low":
      /* Nový klíč (bod 9, 4. 10. 2026): „minimum 10 %, ideál 20 %“ místo
       * „cíl je aspoň 20 %“ (starý klíč bilanceFlag.savingLow ze slovníků odešel). */
      return t("prahyOdkladani.flagPodMinimem", { pct: Math.round(f.val * 100), min: ODKLAD_MIN_PCT, ideal: ODKLAD_IDEAL_PCT });
    case "kids_no_life":
      return t("bilanceFlag.kidsNoLife");
    default:
      return "";
  }
}
